import { createContext, useCallback, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "launchpad_theme";

function readInitial() {
  if (typeof window === "undefined") return "dark";
  // Public routes should always render in dark mode (client on their phone,
  // no context for the owner's theme preference)
  const path = window.location.pathname;
  if (path.startsWith("/firmar/") || path.startsWith("/recibo/") || path.startsWith("/mi-cuenta/")) return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  // Follow system preference on first ever visit
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitial);

  useEffect(() => {
    const html = document.documentElement;
    if (theme === "dark") {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }
    // Don't persist theme when the user is on a public signing page —
    // the client's temporary "dark" shouldn't overwrite the owner's saved preference.
    const onPublic = typeof window !== "undefined" &&
      (window.location.pathname.startsWith("/firmar/") ||
       window.location.pathname.startsWith("/recibo/") ||
       window.location.pathname.startsWith("/mi-cuenta/"));
    if (!onPublic) {
      try {
        window.localStorage.setItem(STORAGE_KEY, theme);
      } catch (e) { /* ignore quota errors */ }
    }
    // Also update PWA theme-color meta so status bar matches
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#0A0B10" : "#F5F7FB");
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === "light" ? "light" : "dark");
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle, isDark: theme === "dark" }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
